import { Injectable, inject } from '@angular/core';
import { catchError, filter, map, Observable, of, take } from 'rxjs';
import { BreadcrumbResolver } from './breadcrumb-resolver';
import { Breadcrumb } from '@tc-angular/design-system';
import { ENQUIRIES_FACADE, GetEnquiryParams } from '../../services/facades';
import { BasicDataResult } from '../../services/data-orchestrator';
import { isNullOrWhitespace } from '../../../shared/helpers/is-null-or-whitespace';
import { EnquiriesRouteSegments } from '../../../shared/constants';
import { EnquiryDto } from '../../../shared/dtos/enquiry/enquiry.dto';
import { mapEnquiryDtoToModel } from '../../../shared/mappers/enquiry.mapper';
import { EnquiryModel } from '../../../shared/models';
import { validate as uuidValidate } from 'uuid';

@Injectable({ providedIn: 'root' })
export class EnquiriesBreadcrumbResolver implements BreadcrumbResolver {
  private readonly enquiriesFacade = inject(ENQUIRIES_FACADE);

  private readonly baseBreadcrumbs: Breadcrumb[] = [
    {
      label: 'nav.dashboard',
      routerLink: ['/'],
    },
    {
      label: 'nav.enquiries',
      routerLink: ['/', EnquiriesRouteSegments.Enquiries],
    },
  ];

  public resolve(params: unknown): Observable<Breadcrumb[]> {
    if (!this.validRouteParams(params)) {
      return of(this.baseBreadcrumbs);
    }

    const enquiryParams = {
      readFromCacheFirst: true,
      id: params.enquiryId,
    } satisfies GetEnquiryParams;

    // TODO: we don't cache this data so this triggers a new API request
    return this.enquiriesFacade.getEnquiry(enquiryParams).data$.pipe(
      filter((result: BasicDataResult<EnquiryDto>) => !result.loading),
      take(1),
      map((result: BasicDataResult<EnquiryDto>) =>
        result.error ? this.baseBreadcrumbs : this.mapDataToBreadcrumbs(params, result.data),
      ),
      catchError((err: unknown) => {
        console.error('Failed to resolve breadcrumb generation', err, params);
        return of(this.baseBreadcrumbs);
      }),
    );
  }

  private validRouteParams(params: unknown): params is EnquiriesRouteParams {
    if (!params || typeof params !== 'object') {
      return false;
    }

    const p = params as Record<string, unknown>;

    if (typeof p.enquiryId !== 'string' || !uuidValidate(p.enquiryId)) {
      return false;
    }

    if (p.quoteId !== undefined && (typeof p.quoteId !== 'string' || !uuidValidate(p.quoteId))) {
      return false;
    }

    return true;
  }

  private mapDataToBreadcrumbs(params: EnquiriesRouteParams, enquiry: EnquiryDto): Breadcrumb[] {
    const enquiryModel = mapEnquiryDtoToModel(enquiry);

    return isNullOrWhitespace(params.quoteId)
      ? this.mapEnquiryDataToBreadcrumbs(enquiryModel)
      : this.mapQuoteDataToBreadcrumbs(params, enquiryModel);
  }

  private mapEnquiryDataToBreadcrumbs(enquiry: EnquiryModel): Breadcrumb[] {
    return [
      ...this.baseBreadcrumbs,
      {
        label: enquiry.name,
        routerLink: undefined,
      } satisfies Breadcrumb,
    ];
  }

  private mapQuoteDataToBreadcrumbs(params: EnquiriesRouteParams, enquiry: EnquiryModel): Breadcrumb[] {
    const matchedQuote = enquiry.quotes.find((q) => q.id === params.quoteId);
    if (!matchedQuote) {
      throw new Error(`No quote matched id '${params.quoteId}' inside enquiry '${params.enquiryId}'`);
    }

    return [
      ...this.baseBreadcrumbs,
      {
        label: matchedQuote.name,
        routerLink: undefined,
        backNavigationEnabled: true,
      },
    ];
  }
}

interface EnquiriesRouteParams {
  readonly enquiryId: string;
  readonly quoteId?: string;
}
